import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress,
  Alert, Card, CardContent, Grid, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Award, Download, Share2, CheckCircle, RefreshCw } from 'lucide-react';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ACHIEVEMENT_ICONS = {
  questions_100: '📚', questions_500: '🎓', questions_1000: '🏆',
  accuracy_80: '🎯', grade_complete: '⭐', streak_30: '🔥', topic_mastery: '💎'
};

function CertificatesContent() {
  const { user, token } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [newCerts, setNewCerts] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const fetchCerts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/certificates`, { headers });
      const data = await res.json();
      if (data.success) setCerts(data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const checkForNew = async () => {
    setChecking(true);
    try {
      const grade = user?.profile?.grade || 8;
      const res = await fetch(`${API_URL}/certificates/check`, {
        method: 'POST', headers, body: JSON.stringify({ grade })
      });
      const data = await res.json();
      if (data.success && data.data.count > 0) {
        setNewCerts(data.data.newCertificates);
        await fetchCerts();
      } else {
        setNewCerts([]);
      }
    } catch (e) { console.error(e); }
    setChecking(false);
  };

  const downloadCert = (id) => {
    window.open(`${API_URL}/certificates/${id}/download`, '_blank');
  };

  const shareCert = (cert) => {
    const url = `${window.location.origin}/api/certificates/verify/${cert.certificateId}`;
    if (navigator.share) {
      navigator.share({ title: `MathZA Certificate — ${cert.achievementTitle}`, text: cert.achievementDescription, url });
    } else {
      navigator.clipboard.writeText(url);
      alert('Verification link copied to clipboard!');
    }
  };

  useEffect(() => { fetchCerts(); }, []);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Award size={28} color="#6C63FF" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Certificates</Typography>
          <Typography variant="body2" color="text.secondary">Your earned achievements</Typography>
        </Box>
        <Button variant="outlined" onClick={checkForNew} disabled={checking}
          startIcon={checking ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
          sx={{ textTransform: 'none' }}>
          Check for new
        </Button>
      </Box>

      {newCerts.length > 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          🎉 You earned {newCerts.length} new certificate{newCerts.length > 1 ? 's' : ''}!
          {newCerts.map(c => ` ${c.achievementTitle}`).join(',')}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : certs.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 3 }}>
          <Award size={48} color="#ccc" />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>No certificates yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Keep studying! Certificates are awarded for milestones like answering 100 questions in a topic or achieving 80%+ accuracy.
          </Typography>
          <Button variant="contained" onClick={checkForNew} sx={{ textTransform: 'none' }}>
            Check if I earned any
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {certs.map(cert => (
            <Card key={cert._id} elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2,
                '&:hover': { borderColor: 'primary.light' }, transition: 'border-color 0.2s' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: '2rem' }}>
                    {ACHIEVEMENT_ICONS[cert.achievementType] || '🏅'}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{cert.achievementTitle}</Typography>
                    <Typography variant="body2" color="text.secondary">{cert.achievementDescription}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      {cert.grade && <Chip label={`Grade ${cert.grade}`} size="small" variant="outlined" />}
                      {cert.topic && <Chip label={cert.topic} size="small" />}
                      <Chip label={new Date(cert.earnedAt).toLocaleDateString('en-ZA')} size="small" variant="outlined" />
                      <Chip label={cert.certificateId} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={() => downloadCert(cert._id || cert.certificateId)}
                      startIcon={<Download size={14} />} sx={{ textTransform: 'none' }}>
                      Download
                    </Button>
                    <Button size="small" onClick={() => shareCert(cert)}
                      startIcon={<Share2 size={14} />} sx={{ textTransform: 'none' }}>
                      Share
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function CertificatesPage() {
  return (
    <FeatureGate feature="certificates" fallback={<UpgradePrompt feature="certificates" />}>
      <CertificatesContent />
    </FeatureGate>
  );
}
